import React, { useEffect, useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  tooltipClasses,
  CircularProgress,
  Badge,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import ActionsDropdown from "../../../../core/common/crmComponents/ActionsDropdown";
import {
  MODULES,
  MODULES_ACTIONS,
} from "../../../../core/data/constants/moduleConstants";
import LeadApi from "../api/LeadApi";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import HasPermission from "../../../../core/common/wrapper/HasPermission";
import {
  CALLER,
  COMPANY_ADMIN,
  SUPER_ADMIN,
  TEAM_MANAGER,
} from "../../../../core/data/constants/constant";
import { useCallHandler } from "../../../../utils/hooks/useCallHandler";
import { getAllUrlParams } from "../../../../utils/helpers/setAndGetUrlParams";
import {
  convertSecondsToHMS,
  convertToUTCString,
} from "../../../../utils/helpers/helper";
import { useUsersByRole } from "../../manageUsers/hooks/useUserByRole";
import { setFilterModel } from "../../../../core/data/redux/filterSlice";
import { customMuiDateFilter } from "../../../../utils/helpers/customMuiDateFilter";
import { HiDuplicate } from "react-icons/hi";
import {
  formatEnrollmentData,
  formatLeadsData,
  formatProfilingData,
} from "../../../../utils/helpers/formatModulesData";
import SocialMediaIcons from "../../../../core/common/crmComponents/SocialMediaIcons";
import { FiClock, FiSettings } from "react-icons/fi";
import useReminders from "../../reminders/hooks/useReminders";
import { FaCheck, FaMinus } from "react-icons/fa";
import ZoomMeetingStatus from "../../../../core/common/crmComponents/zoomMeeting/ZoomMeetingStatus";
import { useFunctionalityAccess } from "../../../../core/common/wrapper/HasFunctionalityAccess";

const LeadList = ({ leadsData, selectedCompany, setFilteredData }) => {
  const route = all_routes;
  const { user } = useSelector((state) => state?.auth);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [isOpenPostCallModal, setIsOpenPostCallModal] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [shareData, setShareData] = useState({});
  const [hasMore, setHasMore] = useState(true);
  const [limit, setLimit] = useState(10);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const { mutate, isPending, isSuccess } = useCallHandler(
    MODULES.PROFILINGFORM,
    selectedLeadId
  );
  const hasShareLead = useFunctionalityAccess("shareLeads");
  const openModal = () => setIsOpenPostCallModal(true);
  const closeModal = () => setIsOpenPostCallModal(false);

  const { reminders } = useReminders();

  const filterModel = useSelector((state) => state.filter.filterModel);
  const dispatch = useDispatch();

  const allParams = getAllUrlParams(); // Get all URL params
  const queryString = new URLSearchParams(allParams).toString();
  const canChangeLeadOwner = useFunctionalityAccess("changeLeadOwnerShip");
  const hasZoomAccess = useFunctionalityAccess("zoomSetting");
  const hasCallPermission = useFunctionalityAccess("CallPermission");

  const queryClient = useQueryClient();

  // Flatten the leadsData array
  const processedRows = formatProfilingData(leadsData);

  const columns = [
    {
      field: "isInEnrollment",
      headerName: "Is In Enrollment",
      width: 210,
      renderCell: (params) => (
        <div>{params?.row?.isInEnrollment ? "Yes" : "No"}</div>
      ),
    },

    {
      field: "fullName",
      headerName: "Client Name",
      width: 180,
      renderCell: (params) => {
        const leadDetailsUrl = `${route.profilingDetails.replace(
          ":id",
          params.row.id
        )}?${queryString}`;

        return (
          <div className="d-flex align-items-center">
            <Link
              className="text-capitalize list-link text-truncate"
              to={leadDetailsUrl}
              style={{
                maxWidth: "250px", // Adjust width as needed
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {params.row.fullName || "N/A"}
            </Link>
          </div>
        );
      },
    },
    {
      field: "mobile",
      headerName: "Phone",
      width: 160,
      renderCell: (params) => (
        <div>
          <p className="mb-0">{params?.row?.mobile || "N/A"}</p>
        </div>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      width: 200,
      renderCell: (params) => (
        <div className="text-capitalize">{params?.row?.email || "N/A"}</div>
      ),
    },
    {
      field: "isCompleted",
      headerName: "Is Profiling Form Completed",
      width: 200,
      renderCell: (params) => (
        <div className="text-capitalize">
          {params?.row?.isCompleted ? "yes" : "no"}
        </div>
      ),
    },

    {
      field: "submittedOn",
      headerName: "Submitted On",
      width: 210,
      valueFormatter: (params) =>
        params?.value ? convertToUTCString(params?.value) : "",
      filterOperators: customMuiDateFilter,
      renderCell: (params) => (
        <div>
          {params?.row?.submittedOn
            ? new Date(params?.row?.submittedOn).toLocaleDateString()
            : "N/A"}
        </div>
      ),
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <DataGrid
        rows={processedRows}
        columns={columns.map((column) => ({
          ...column,
          headerClassName: "bg-light-400 ",
        }))}
        autoHeight
        hideFooter
        disableExtendRowFullWidth
        onStateChange={(state) => {
          const filteredRowIds = Object.entries(state.filter.filteredRowsLookup)
            .filter(([_, isVisible]) => isVisible === true)
            .map(([rowId]) => rowId); // Keep IDs as strings

          // Filter leadsData based on these IDs
          const filteredData = leadsData?.filter((row) =>
            filteredRowIds.includes(row?._id)
          );
          // Pass the filtered data to the parent
          // setFilteredData(filteredData);
        }}
        sx={{
          "& .sticky-column": {
            position: "sticky",
            right: 0,
            background: "white", // Prevents content from hiding under it
            zIndex: 100, // Keeps it on top
          },
          "& .MuiDataGrid-columnHeaders": {
            background: "white",
            position: "sticky",
            top: 0,
            zIndex: 110,
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "auto",
          },
        }}
      />
    </div>
  );
};

export default LeadList;
